import { useEffect, useRef } from 'react'

import type { Mikrocopy } from '@/lib/api'

const MAX_ZEICHEN = 1000
const WARNSCHWELLE = 0.85

interface Props {
  wert: string
  onAendern: (wert: string) => void
  onAbsenden: () => void
  mikrocopy: Mikrocopy
  laeuft: boolean
  bereit: boolean
}

export function Eingabe({ wert, onAendern, onAbsenden, mikrocopy, laeuft, bereit }: Props) {
  const feld = useRef<HTMLTextAreaElement>(null)

  // Das Erste, was man sieht, ist ein Eingabefeld — also gehört der Fokus
  // auch dorthin.
  useEffect(() => {
    feld.current?.focus()
  }, [])

  const zeichen = wert.length
  const knapp = zeichen > MAX_ZEICHEN * WARNSCHWELLE
  const zuLang = zeichen > MAX_ZEICHEN
  const absendbar = bereit && !laeuft && wert.trim().length > 0 && !zuLang

  return (
    <form
      onSubmit={(ereignis) => {
        ereignis.preventDefault()
        if (absendbar) onAbsenden()
      }}
      className="bg-surface-container rounded-card border-outline border p-5 sm:p-6"
    >
      <label htmlFor="eingabe" className="sr-only">
        Text, der übertragen werden soll
      </label>
      <textarea
        id="eingabe"
        ref={feld}
        value={wert}
        onChange={(ereignis) => {
          onAendern(ereignis.target.value)
        }}
        onKeyDown={(ereignis) => {
          // ⌘/Strg + Enter sendet ab, ohne dass man zur Maus greifen muss.
          if ((ereignis.metaKey || ereignis.ctrlKey) && ereignis.key === 'Enter' && absendbar) {
            ereignis.preventDefault()
            onAbsenden()
          }
        }}
        rows={3}
        placeholder={mikrocopy.leer}
        aria-describedby="eingabe-hinweis eingabe-zaehler"
        aria-invalid={zuLang}
        className="font-display placeholder:text-on-surface-muted/70 w-full resize-none bg-transparent text-xl leading-snug outline-none sm:text-2xl"
      />

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <p id="eingabe-hinweis" className="text-on-surface-muted max-w-[38ch] text-xs">
          Die Eingabe geht zur Übertragung an Anthropic. Bitte keine sensiblen Daten.
        </p>

        <div className="ml-auto flex items-center gap-3">
          <span
            id="eingabe-zaehler"
            className={`text-xs tabular-nums ${zuLang ? 'text-on-surface font-medium' : 'text-on-surface-muted'}`}
            aria-live={knapp ? 'polite' : 'off'}
          >
            {zeichen} / {MAX_ZEICHEN}
          </span>

          <button
            type="submit"
            disabled={!absendbar}
            className="bg-primary text-on-primary rounded-pill px-6 py-2.5 font-medium transition-transform enabled:hover:scale-[1.02] enabled:active:scale-[0.98] disabled:opacity-40"
          >
            {laeuft ? mikrocopy.laedt : mikrocopy.cta}
          </button>
        </div>
      </div>

      {zuLang && (
        <p role="alert" className="mt-3 text-sm">
          Der Text ist zu lang. Kürze ihn auf {MAX_ZEICHEN} Zeichen oder teile ihn auf.
        </p>
      )}
    </form>
  )
}
